import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Divider, Group, Stack, Text } from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { listMonitoringLinks } from '../../api/resources';
import { DataPanel } from '../DataPanel';
import { formatDateTime, linkEndLabel } from '../../lib/monitoringLinks';
import type { CaseRecord, MonitoringLinkListItem } from '../../types/api';
import { GenerateMonitoringLinkModal } from './GenerateMonitoringLinkModal';
import { LifecycleBadge, ProgressSummary, RevokeMonitoringLinkModal } from './MonitoringLinkParts';

/** The case's monitoring check links: who they went to, how far each customer got, and the actions. */
export function MonitoringLinksSection({ caseRecord }: { caseRecord: CaseRecord }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [generateOpen, setGenerateOpen] = useState(false);
    const [revoking, setRevoking] = useState<MonitoringLinkListItem | null>(null);

    const linksQ = useQuery({
        queryKey: ['monitoring-links', { caseId: caseRecord.id }],
        queryFn: () => listMonitoringLinks({ caseId: caseRecord.id }),
    });
    const links = linksQ.data ?? [];

    return (
        <DataPanel>
            <Group justify="space-between" align="center" mb="md" wrap="nowrap">
                <Text fw={700} size="md">
                    {t('features.monitoringLinks.sectionTitle')}
                </Text>
                <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconSend size={14} />}
                    onClick={() => setGenerateOpen(true)}
                >
                    {t('features.monitoringLinks.sendAction')}
                </Button>
            </Group>

            {links.length === 0 ? (
                <Text size="sm" c="dimmed">
                    {linksQ.isLoading
                        ? t('common.loading.default')
                        : t('features.monitoringLinks.none')}
                </Text>
            ) : (
                <Stack gap={0}>
                    {links.map((link, index) => (
                        <Box key={link.id}>
                            {index > 0 ? <Divider /> : null}
                            <Stack gap={6} py="sm">
                                <Group justify="space-between" wrap="nowrap" align="flex-start">
                                    <Text size="sm" fw={700}>
                                        {link.subjectName ?? t('entities.subject')}
                                    </Text>
                                    <LifecycleBadge lifecycle={link.lifecycle} />
                                </Group>
                                <ProgressSummary link={link} />
                                <Group justify="space-between" wrap="wrap" gap="xs">
                                    <Text size="xs" c="dimmed">
                                        {[
                                            `${t('features.monitoringLinks.sent')} ${formatDateTime(link.createdAt) ?? ''}`,
                                            linkEndLabel(t, link),
                                        ].join(' · ')}
                                    </Text>
                                    <Group gap="xs">
                                        <Button
                                            size="compact-xs"
                                            variant="subtle"
                                            onClick={() => navigate(`/visits/${link.visitId}`)}
                                        >
                                            {t('features.monitoringLinks.viewVisit')}
                                        </Button>
                                        {link.lifecycle === 'live' ? (
                                            <Button
                                                size="compact-xs"
                                                variant="subtle"
                                                color="red"
                                                onClick={() => setRevoking(link)}
                                            >
                                                {t('features.monitoringLinks.revokeAction')}
                                            </Button>
                                        ) : null}
                                    </Group>
                                </Group>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}

            <GenerateMonitoringLinkModal
                opened={generateOpen}
                onClose={() => setGenerateOpen(false)}
                caseRecord={caseRecord}
                existingLinks={links}
            />
            <RevokeMonitoringLinkModal link={revoking} onClose={() => setRevoking(null)} />
        </DataPanel>
    );
}
